import type { ProjectCommentNotificationPayload } from '../api/notifications'
import {
  COMMENT_ID_QUERY_KEY,
  COMMENT_TAB_QUERY_KEY,
  DISCUSSION_QUERY_KEY,
  DISCUSSION_QUERY_VALUE,
  NOTIF_SCROLL_SEQ_QUERY_KEY,
  PARENT_COMMENT_ID_QUERY_KEY,
  SCROLL_QUERY_KEY,
  SCROLL_QUERY_VALUE,
} from '../consts/commentNotificationQueryKeys'
import { routesProfile } from '../consts/routesPath'
import { CommentsType } from '../types/common'

export function mapNotificationCommentTypeToTab(type: string | undefined): CommentsType {
  switch ((type ?? '').toLowerCase()) {
    case 'question':
      return CommentsType.questions
    case 'suggestion':
      return CommentsType.suggestions
    case 'opinion':
      return CommentsType.opinions
    default:
      return CommentsType.opinions
  }
}

export function parsePositiveIntParam(value: string | null): number | undefined {
  if (!value) return undefined
  const n = Number.parseInt(value, 10)
  return Number.isFinite(n) && n > 0 ? n : undefined
}

export function parseCommentTabParam(value: string | null): CommentsType | undefined {
  if (!value) return undefined
  if (Object.values(CommentsType).includes(value as CommentsType)) {
    return value as CommentsType
  }
  return undefined
}

export function stripNotificationCommentParams(prev: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(prev)
  next.delete(DISCUSSION_QUERY_KEY)
  next.delete(COMMENT_TAB_QUERY_KEY)
  next.delete(PARENT_COMMENT_ID_QUERY_KEY)
  next.delete(COMMENT_ID_QUERY_KEY)
  next.delete(SCROLL_QUERY_KEY)
  next.delete(NOTIF_SCROLL_SEQ_QUERY_KEY)
  return next
}

export function buildProjectCommentNotificationUrl(
  slug: string,
  payload: ProjectCommentNotificationPayload,
  opts: { isMobile: boolean },
): string {
  const base = `/${routesProfile.PROJECTS.path}/${slug}`
  if (!opts.isMobile) {
    const params = new URLSearchParams()
    params.set(SCROLL_QUERY_KEY, SCROLL_QUERY_VALUE)
    params.set(NOTIF_SCROLL_SEQ_QUERY_KEY, String(Date.now()))
    params.set(COMMENT_TAB_QUERY_KEY, mapNotificationCommentTypeToTab(payload.comment_type))
    if (payload.comment_id != null && payload.comment_id > 0) {
      params.set(COMMENT_ID_QUERY_KEY, String(payload.comment_id))
    }
    const parentId = payload.parent_comment_id
    if (typeof parentId === 'number' && parentId > 0) {
      params.set(PARENT_COMMENT_ID_QUERY_KEY, String(parentId))
    }
    const search = params.toString()
    return search ? `${base}?${search}` : base
  }

  const params = new URLSearchParams()
  params.set(DISCUSSION_QUERY_KEY, DISCUSSION_QUERY_VALUE)
  params.set(COMMENT_TAB_QUERY_KEY, mapNotificationCommentTypeToTab(payload.comment_type))
  if (payload.comment_id != null && payload.comment_id > 0) {
    params.set(COMMENT_ID_QUERY_KEY, String(payload.comment_id))
  }
  const parentId = payload.parent_comment_id
  if (typeof parentId === 'number' && parentId > 0) {
    params.set(PARENT_COMMENT_ID_QUERY_KEY, String(parentId))
  }

  const search = params.toString()
  return search ? `${base}?${search}` : base
}

export function buildSocialOrderCommentNotificationUrl(
  slug: string,
  payload: Pick<
    ProjectCommentNotificationPayload,
    'comment_type' | 'comment_id' | 'parent_comment_id'
  >,
  opts: { isMobile: boolean },
): string {
  const base = `/${routesProfile.SOCIALORDERS.path}/${slug}`
  if (!opts.isMobile) {
    const params = new URLSearchParams()
    params.set(SCROLL_QUERY_KEY, SCROLL_QUERY_VALUE)
    params.set(NOTIF_SCROLL_SEQ_QUERY_KEY, String(Date.now()))
    params.set(COMMENT_TAB_QUERY_KEY, mapNotificationCommentTypeToTab(payload.comment_type))
    if (payload.comment_id != null && payload.comment_id > 0) {
      params.set(COMMENT_ID_QUERY_KEY, String(payload.comment_id))
    }
    const parentId = payload.parent_comment_id
    if (typeof parentId === 'number' && parentId > 0) {
      params.set(PARENT_COMMENT_ID_QUERY_KEY, String(parentId))
    }
    const search = params.toString()
    return search ? `${base}?${search}` : base
  }

  const params = new URLSearchParams()
  params.set(DISCUSSION_QUERY_KEY, DISCUSSION_QUERY_VALUE)
  params.set(COMMENT_TAB_QUERY_KEY, mapNotificationCommentTypeToTab(payload.comment_type))
  if (payload.comment_id != null && payload.comment_id > 0) {
    params.set(COMMENT_ID_QUERY_KEY, String(payload.comment_id))
  }
  const parentId = payload.parent_comment_id
  if (typeof parentId === 'number' && parentId > 0) {
    params.set(PARENT_COMMENT_ID_QUERY_KEY, String(parentId))
  }

  const search = params.toString()
  return search ? `${base}?${search}` : base
}
